/**
 * The live vehicle events this browser has toasted, for the notifications page and the sidebar
 * badge.
 *
 * A module-level external store, like `dismissed-store.ts`, for the same reasons: the badge, the
 * page and the app-wide listener all read it, and `localStorage` must stay out of render. Unlike
 * cost reminders there is no upstream list behind these — the socket delivers an event once — so
 * the entries themselves live here, capped, and keyed per user so two accounts sharing a browser
 * never see each other's feed.
 *
 * Entries keep the raw event string rather than rendered text, so a language switch relabels them.
 */

export interface StoredVehicleEvent {
  /** `${vehicleId}|${occurredAt}|${raw}` — also the dedupe key, so nothing is recorded twice. */
  id: string;
  vehicleId: string;
  /** The event exactly as the socket sent it, e.g. `nogo_zone_enter:9`. */
  raw: string;
  /** ISO timestamp of the telemetry point that raised it. */
  occurredAt: string;
  /** Speed (km/h) on that point, when the update carried one — shown against a speeding limit. */
  speed?: number;
  /**
   * The point's `driver_id` (`id:5`, `c:123`, `""` for none). Absent when the update did not carry
   * the field, in which case the vehicle's cached driver is shown instead.
   */
  driverRef?: string;
  /** Where the point that raised it was. Absent on entries recorded before positions were kept. */
  lat?: number;
  lon?: number;
  read: boolean;
}

const STORAGE_PREFIX = "fleettrack-vehicle-events:";
const MAX_EVENTS = 200;

const EMPTY: readonly StoredVehicleEvent[] = [];

let userId: string | null = null;
let snapshot: readonly StoredVehicleEvent[] = EMPTY;
const listeners = new Set<() => void>();

function storageKey(id: string) {
  return `${STORAGE_PREFIX}${id}`;
}

function isStoredEvent(value: unknown): value is StoredVehicleEvent {
  if (!value || typeof value !== "object") return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.id === "string" &&
    typeof entry.vehicleId === "string" &&
    typeof entry.raw === "string" &&
    typeof entry.occurredAt === "string" &&
    typeof entry.read === "boolean" &&
    (entry.speed === undefined || typeof entry.speed === "number") &&
    (entry.driverRef === undefined || typeof entry.driverRef === "string") &&
    (entry.lat === undefined || typeof entry.lat === "number") &&
    (entry.lon === undefined || typeof entry.lon === "number")
  );
}

function read(id: string): readonly StoredVehicleEvent[] {
  if (typeof window === "undefined") return EMPTY;
  try {
    const raw = window.localStorage.getItem(storageKey(id));
    if (!raw) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return EMPTY;
    return parsed.filter(isStoredEvent).slice(0, MAX_EVENTS);
  } catch {
    return EMPTY;
  }
}

function write(events: readonly StoredVehicleEvent[]) {
  if (typeof window === "undefined" || userId === null) return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(events));
  } catch {
    // A full or blocked store is not worth failing over; the feed stays in memory.
  }
}

function emit() {
  // Copied: a listener may unsubscribe while we iterate.
  for (const listener of [...listeners]) listener();
}

function update(next: readonly StoredVehicleEvent[]) {
  snapshot = next;
  write(next);
  emit();
}

/** Points the store at the signed-in user's feed; `null` on logout empties it. */
export function setVehicleEventUser(id: string | null) {
  if (id === userId) return;
  userId = id;
  snapshot = id === null ? EMPTY : read(id);
  emit();
}

export function subscribeVehicleEvents(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Newest first. Identity only changes on a write, as `useSyncExternalStore` requires. */
export function getVehicleEventsSnapshot(): readonly StoredVehicleEvent[] {
  return snapshot;
}

export function getVehicleEventsServerSnapshot(): readonly StoredVehicleEvent[] {
  return EMPTY;
}

/**
 * Records new events and returns the ones that were actually new — the caller toasts only those,
 * so a redelivered update is never toasted twice.
 */
export function addVehicleEvents(
  events: readonly Omit<StoredVehicleEvent, "read">[],
): StoredVehicleEvent[] {
  if (userId === null || events.length === 0) return [];

  const known = new Set(snapshot.map((entry) => entry.id));
  const added: StoredVehicleEvent[] = [];
  for (const event of events) {
    if (known.has(event.id)) continue;
    known.add(event.id);
    added.push({ ...event, read: false });
  }
  if (added.length === 0) return [];

  // Arrival order is oldest first; the feed is newest first.
  update([...[...added].reverse(), ...snapshot].slice(0, MAX_EVENTS));
  return added;
}

function setRead(ids: ReadonlySet<string>, read: boolean) {
  let changed = false;
  const next = snapshot.map((entry) => {
    if (!ids.has(entry.id) || entry.read === read) return entry;
    changed = true;
    return { ...entry, read };
  });
  if (changed) update(next);
}

export function markVehicleEventRead(id: string) {
  setRead(new Set([id]), true);
}

export function markVehicleEventUnread(id: string) {
  setRead(new Set([id]), false);
}

export function markVehicleEventsRead(ids: readonly string[]) {
  setRead(new Set(ids), true);
}
